import React, { useState } from 'react';
import { Button, Form, Input, Modal, Skeleton, Space, Table, Tooltip, Typography } from 'antd';
import useToastContext from '../../hooks/useToastContext';
import { ICard, OwnershipLevel, OwnershipLevelType } from '../../interfaces/card';
import { CardsFiltersParam} from './interfaces/CardsFiltersParam'
import { useNavigate, useParams } from 'react-router-dom';
import { getDateFormatted, sortDateDesc } from '../../utils/date';
import { CardsQueryParamsHeaders, CARD_STATE_TO_HUMAN_TEXT, CARD_TYPE_TO_HUMAN_TEXT, RealmRoles } from '../../constants/index';
import useNominateCard from '../../hooks/useNominateCard';
import useAuthService from '../../hooks/useAuthService';
import { CreditCardOutlined, EyeOutlined, WalletOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { Icon } from '@orbita-ui/core';
import styled from 'styled-components';
import { CardHolderCardsContainer, CardHolderCardsContainerText } from '../card.styles';
import { SectionTitleContainer, SectionTitle, SectionTitleActions, NoSpaceDivider} from '../styles'
import { isNil } from 'lodash';
import useGetAllCards from '../../hooks/data/useGetAllCards';
import { NewCardParams } from './interfaces/NewCardParams';

const CardHolderCardActionsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1.5rem;
`;


/* eslint-disable camelcase */
type NominateCardFormInputs = {
    fourth_line: number;
    last_four: string;
};


const NewNominateCardForm = ({ onCancel, cardHolderId }) => {
    const toast = useToastContext();
    const [form] = Form.useForm();
    const { nominateLoading: isLoading, nominateError: error, nominateCard } = useNominateCard();

    const onSubmit = async (data: NominateCardFormInputs) => {
      try {
        const response = await nominateCard({ ...data, card_holder_id: cardHolderId });

        if (response.status === 200) {
          toast.success('Exito', 'La tarjeta se ha nominado satisfactoriamente!.');
          form.resetFields();
          onCancel();
        } else {
          throw new Error();
        }
      } catch (e: unknown) {
        toast.error('Error', `Ocurrió un error: ${error}, porfavor, intente nuevamente.`);
      }
    };

    return (
      <Form<NominateCardFormInputs>
        name="basic"
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: '100%', marginTop: '1rem' }}
        initialValues={{ remember: true }}
        onFinish={onSubmit}
        onFinishFailed={console.log}
        autoComplete="off"
      >
        <Typography.Title level={3}>Nominar tarjeta</Typography.Title>

        <Form.Item<NominateCardFormInputs>
          label="Últimos 4"
          name="last_four"
          rules={[{ required: true, message: 'Por favor ingresar cuatro digitos válidos.', max: 4 }]}
        >
          <Input />
        </Form.Item>
        <Form.Item<NominateCardFormInputs>
          label="Cuarta linea"
          name="fourth_line"
          rules={[{ required: true, message: 'Por favor ingresar cuatro digitos válidos.', min: 1 }]}
        >
          <Input />
        </Form.Item>
        <Button style={{ textAlign: 'center' }} type="primary" htmlType="submit" loading={isLoading}>
          Asignar
        </Button>
      </Form>
    );
  };




const parameterToParamMap = {
    cardholder: CardsQueryParamsHeaders.CARDHOLDER_ID,
    cardholderParent: CardsQueryParamsHeaders.CARDHOLDER_PARENT_ID,
    cardParent:CardsQueryParamsHeaders.CARD_PARENT,
    ownershipLevel: CardsQueryParamsHeaders.OWNERSHIP_LEVEL
};

const CardsTable = ({ searchParam, tableTitle, showNominateCard, newCardParams, style}: { searchParam: CardsFiltersParam, tableTitle?: string , showNominateCard?: boolean, newCardParams?: NewCardParams | undefined, style?: React.CSSProperties}) => {
    const title = tableTitle || "Mis Tarjetas";
    const nominatedCard = showNominateCard || false;
    const { id, issuer } = useParams<{ id: string; issuer: string }>();
    const navigate = useNavigate();
    const toast = useToastContext();
    const { userHasRole } = useAuthService();
    const [nominateCardsModalOpen, setNominateCardsModalOpen] = useState<boolean>(false);

    const queryParams = new URLSearchParams();

    if(!isNil(newCardParams)){
      Object.entries(newCardParams).forEach(([param, value]) => {
        if (value && param in parameterToParamMap) {
          queryParams.append(parameterToParamMap[param], value)
        }
      });
    }

    var newCardUrl = `/${issuer}/card-holders/cards/new?${queryParams.toString()}`;

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
      });


    const { cards, pageInfo, isLoading } = useGetAllCards(
        paginationModel.page,
        paginationModel.pageSize,
        searchParam
    );

    const onViewCardButtonClick = (cardId: string) => {
        localStorage.setItem('chId', id);
        navigate(`/${issuer}/cards/${cardId}`);
    };

    const onViewTransactionsButtonClick = (cardId: string) => {
        navigate(`/${issuer}/transactions/${cardId}`);
    };

    const onViewAdditionalCardholder = (cardHolderParentId: string) => {
      navigate(`/${issuer}/card-holders/${cardHolderParentId}`);
    }

    const handlePaginationChange = (page: number, pageSize: number) => {
        setPaginationModel({ page: page - 1, pageSize });
      };

    const columns: ColumnsType<ICard> = [
        {
          dataIndex: 'card_id',
          key: 'card_id',
          title: 'ID',
        },
        {
          dataIndex: 'created_time',
          key: 'created_time',
          title: 'Fecha de Creacion',
          defaultSortOrder: 'descend',
          sorter: (a, b) => sortDateDesc(b.created_time, a.created_time),
          render: (_, record) => {
            return getDateFormatted(record.created_time);
          },
        },
        {
          dataIndex: 'card_type',
          key: 'card_type',
          title: 'Tipo',
          render: (_, record) => {
            return CARD_TYPE_TO_HUMAN_TEXT[record.card_type];
          },
        },
        {
          dataIndex: 'state',
          key: 'state',
          title: 'Estado',
          width: 150,
          render: (_, record) => {
            return CARD_STATE_TO_HUMAN_TEXT[record.state];
          },
        },
        { dataIndex: 'last_four', key: 'last_four', title: 'Ultimos 4 Digitos' },
        {
          dataIndex: 'ownership_level',
          title: 'Ownership',
          key: 'ownership_level',
          render: (_, record) => {
            return (
              <div style={{ textAlign: 'center' }}>
                {
                  OwnershipLevelType.Main === record?.ownership_level ? (
                    <Space> {OwnershipLevel.MAIN} </Space>
                  ) : (
                    title === "Adicionales" ? (
                      <Space> {OwnershipLevel.ADDITIONAL} </Space>
                    ) : (
                      <Tooltip trigger={['hover']} title={"Ver Cardholder Parent"} placement="topLeft">
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            onViewAdditionalCardholder(record?.cardholder_parent);
                          }}
                          size="small"
                          type="link"
                        >
                          {OwnershipLevel.ADDITIONAL}
                        </Button>
                      </Tooltip>
                    )
                  )
                }
              </div>
            );
          }},
        {
          dataIndex: 'actions',
          key: 'actions',
          title: 'Acciones',
          render: (_, record) => {
            return (
              <Space>
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    onViewTransactionsButtonClick(record.card_id);
                  }}
                  size="small"
                  type="link"
                  icon={<WalletOutlined />}
                >
                  Transacciones
                </Button>
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    onViewCardButtonClick(record.card_id);
                  }}
                  size="small"
                  type="link"
                  icon={<EyeOutlined />}
                >
                  Ver
                </Button>
              </Space>
            );
          },
        },
      ];


  return (
    <>

      {isLoading ? (
        <Skeleton />
      ) : (
        <>
        <SectionTitleContainer style={style}>
            <SectionTitle> {title} </SectionTitle>
            <SectionTitleActions>
                {userHasRole([RealmRoles.CardsUnnamedWrite]) && nominatedCard && (
                <Button
                    style={{ marginRight: 2 }}
                    type="link"
                    icon={<CreditCardOutlined />}
                    onClick={() => setNominateCardsModalOpen(true)}
                >
                    Nominar tarjeta
                </Button>
                )}
                {userHasRole([RealmRoles.CardsAllWrite]) && !isNil(newCardParams) &&(
                <Button
                    type="link"
                    onClick={() => navigate(newCardUrl)}
                >
                    Agregar
                </Button>
                )}
            </SectionTitleActions>
        </SectionTitleContainer>
        <NoSpaceDivider />

          {userHasRole([RealmRoles.CardsAllRead]) ? (
            isNil(cards) ? (
              <CardHolderCardsContainer style={{marginTop:'1em'}}>
                <Icon name="InfoIcon" color="info" size="S" />
                <CardHolderCardsContainerText>El titular no cuenta con tarjetas</CardHolderCardsContainerText>
              </CardHolderCardsContainer>
            ) : (
              <Table style={{marginTop:'1em'}}
                dataSource={cards || []}
                scroll={{ x: true }}
                // @ts-ignore
                columns={columns.filter((e) => Object.keys(e).length > 0)}
                loading={isLoading}
                pagination={{
                    total: pageInfo?.total_rows,
                    showTotal: (total) => pageInfo?.total !== undefined ? `Total: ${total}` : ``,
                    defaultPageSize: 10,
                    defaultCurrent: 1,
                    onChange: handlePaginationChange,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showSizeChanger: true,
                }}
              />
            )
          ) : null}

          <Modal
            open={nominateCardsModalOpen}
            onCancel={() => setNominateCardsModalOpen(false)}
            footer={null}
            width={500}
          >

             <NewNominateCardForm cardHolderId={searchParam.cardholder} onCancel={() => setNominateCardsModalOpen(false)} />
          </Modal>
        </>
      )}

    </>
  )
}

export default CardsTable;
